/**
 * NYT Bestseller Strategy Guide
 * Educational content on becoming a New York Times bestselling author
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Layout } from '../components/Layout';
import { 
  Trophy, BookOpen, TrendingUp, Users, 
  Target, DollarSign, Calendar, CheckCircle2,
  Rocket, Star, Award, Zap, Mail
} from 'lucide-react';

export function NYTBestsellerGuide() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showContactForm, setShowContactForm] = useState(false);

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-purple-50">
        {/* Hero Section */}
        <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="bg-gradient-to-r from-amber-500 to-yellow-500 p-4 rounded-full">
                <Trophy className="w-16 h-16 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              New York Times<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-purple-600">
                Bestseller Strategy
              </span>
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              The complete, no-BS guide to hitting the NYT Bestseller list. Learn the exact strategies, 
              timing, and tactics used by authors who've made it to the top.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid md:grid-cols-4 gap-4 mb-12">
            <Card className="text-center">
              <CardContent className="pt-6">
                <BookOpen className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-gray-900">1,000+</p>
                <p className="text-sm text-gray-600">Books weekly</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-gray-900">$50K+</p>
                <p className="text-sm text-gray-600">Investment needed</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-gray-900">5,000+</p>
                <p className="text-sm text-gray-600">Sales in Week 1</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <Calendar className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-gray-900">52</p>
                <p className="text-sm text-gray-600">Lists per year</p>
              </CardContent>
            </Card>
          </div>

          {/* Reality Check */}
          <Alert className="mb-12 bg-amber-50 border-2 border-amber-300">
            <AlertDescription className="text-amber-900">
              <div className="flex items-start gap-3">
                <Zap className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <strong className="text-lg">Reality Check:</strong>
                  <p className="mt-2">
                    Making the NYT Bestseller list is <strong>NOT about having the best book</strong>. 
                    It's about strategic marketing, timing, and coordinated sales efforts. Many amazing books never 
                    make the list, while books with massive marketing budgets consistently do. This guide shows you 
                    the system.
                  </p>
                </div>
              </div>
            </AlertDescription>
          </Alert>

          {/* The Secret Formula */}
          <Card className="mb-12 border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50">
            <CardHeader>
              <CardTitle className="text-3xl">🎯 The NYT Bestseller Formula</CardTitle>
              <CardDescription className="text-lg">
                What the industry doesn't tell you
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-6 border-2 border-purple-200">
                  <h4 className="font-bold text-xl text-purple-900 mb-3">1. Strategic Launch Timing</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Best months:</strong> September, October, January (avoid December/summer)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Launch day:</strong> Tuesday (gives full week of reportable sales)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Avoid:</strong> Major holidays, big book releases, political events</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 border-2 border-purple-200">
                  <h4 className="font-bold text-xl text-purple-900 mb-3">2. Sales Velocity & Volume</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Week 1 target:</strong> 5,000-10,000+ sales (varies by category and competition)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Velocity matters:</strong> All sales in one week is better than spread out sales</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Reporting stores:</strong> NYT tracks ~3,000 independent bookstores + online</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 border-2 border-purple-200">
                  <h4 className="font-bold text-xl text-purple-900 mb-3">3. Geographic Diversity</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Critical:</strong> Sales must come from multiple states/regions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Red flag:</strong> All sales from one city = looks like "bulk buying"</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Strategy:</strong> Coordinate launches in 10+ cities simultaneously</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 border-2 border-purple-200">
                  <h4 className="font-bold text-xl text-purple-900 mb-3">4. Pre-Orders Strategy</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Amazon:</strong> Pre-orders count in launch week (critical!)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Build momentum:</strong> Start pre-order campaign 6-12 weeks early</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Incentives:</strong> Offer exclusive bonuses for pre-orders</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 border-2 border-purple-200">
                  <h4 className="font-bold text-xl text-purple-900 mb-3">5. Strategic Partnerships & PR</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Media appearances:</strong> Major morning shows, podcasts with 100K+ listeners</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Book tours:</strong> 10-20 city tour in Week 1 (costly but effective)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Influencer partnerships:</strong> Coordinate 50+ influencers to post on launch day</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* The Money Talk */}
          <Card className="mb-12 border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
            <CardHeader>
              <CardTitle className="text-3xl">💰 The Real Budget Breakdown</CardTitle>
              <CardDescription className="text-lg">
                What it actually costs to hit the list
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-gray-900">Professional PR Firm (6 months)</span>
                    <span className="font-bold text-green-600">$15,000 - $50,000</span>
                  </div>
                  <p className="text-sm text-gray-600">Essential for media placements and coordinated campaigns</p>
                </div>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-gray-900">Book Marketing Campaign</span>
                    <span className="font-bold text-green-600">$10,000 - $30,000</span>
                  </div>
                  <p className="text-sm text-gray-600">Facebook/Instagram ads, Amazon ads, email campaigns</p>
                </div>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-gray-900">Book Tour & Events</span>
                    <span className="font-bold text-green-600">$8,000 - $20,000</span>
                  </div>
                  <p className="text-sm text-gray-600">Travel, venues, staffing for 10-20 city tour</p>
                </div>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-gray-900">Influencer Partnerships</span>
                    <span className="font-bold text-green-600">$5,000 - $15,000</span>
                  </div>
                  <p className="text-sm text-gray-600">50+ influencers posting on launch week</p>
                </div>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-gray-900">Book Production (quality matters)</span>
                    <span className="font-bold text-green-600">$3,000 - $10,000</span>
                  </div>
                  <p className="text-sm text-gray-600">Professional editing, cover design, formatting</p>
                </div>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-gray-900">Pre-Launch Buzz Building</span>
                    <span className="font-bold text-green-600">$5,000 - $10,000</span>
                  </div>
                  <p className="text-sm text-gray-600">ARC copies, blogger outreach, pre-order incentives</p>
                  </div>

                <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg p-6 border-2 border-green-300 mt-6">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-gray-900">Total Investment Range:</span>
                    <span className="text-3xl font-bold text-green-600">$50K - $150K</span>
                  </div>
                  <p className="text-sm text-gray-700 mt-2">
                    <strong>Reality:</strong> Traditional publishers absorb most of these costs. Self-published authors need 
                    this budget OR must build these systems organically over 1-2 years.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Alternative Strategies for Indies */}
          <Card className="mb-12 border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardHeader>
              <CardTitle className="text-3xl">🚀 Indie Author Strategies (Lower Budget)</CardTitle>
              <CardDescription className="text-lg">
                How to compete without $100K
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <h4 className="font-bold text-lg text-blue-900 mb-3 flex items-center gap-2">
                    <Rocket className="w-5 h-5" />
                    Strategy 1: Build a Massive Email List First
                  </h4>
                  <p className="text-gray-700 mb-3">
                    Spend 12-24 months building an email list of 20,000+ engaged readers BEFORE your book launches. 
                    Use lead magnets, free chapters, and consistent value delivery.
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Timeline:</strong> 2 years | <strong>Cost:</strong> $500-2,000/month | <strong>Result:</strong> Targeting 5,000+ Week 1 sales
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <h4 className="font-bold text-lg text-blue-900 mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Strategy 2: Coordinate a "Book Club" Launch
                  </h4>
                  <p className="text-gray-700 mb-3">
                    Partner with 100+ book clubs nationwide to all read and purchase your book in the same week. 
                    Offer group discounts, virtual author Q&As, and exclusive discussion guides.
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Timeline:</strong> 6 months | <strong>Cost:</strong> $5,000-10,000 | <strong>Result:</strong> Geographic diversity + volume
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <h4 className="font-bold text-lg text-blue-900 mb-3 flex items-center gap-2">
                    <Star className="w-5 h-5" />
                    Strategy 3: The "BookTok" Viral Approach
                  </h4>
                  <p className="text-gray-700 mb-3">
                    Get 500+ BookTok/BookTube creators to post reviews in your launch week. Send 1,000 ARCs 
                    to micro-influencers (5K-50K followers) 8 weeks before launch.
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Timeline:</strong> 4 months | <strong>Cost:</strong> $8,000-15,000 (ARCs + gifting) | <strong>Result:</strong> Organic reach of 10M+
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <h4 className="font-bold text-lg text-blue-900 mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Strategy 4: The "Category King" Method
                  </h4>
                  <p className="text-gray-700 mb-3">
                    Choose a very specific, less competitive category. Dominate that niche to hit #1 in category, 
                    then use that momentum to climb the main list. (Note: NYT editorializes - not guaranteed)
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Timeline:</strong> 3 months | <strong>Cost:</strong> $10,000-20,000 | <strong>Result:</strong> Better odds in smaller pool
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Common Mistakes */}
          <Card className="mb-12 border-2 border-red-200 bg-gradient-to-br from-red-50 to-pink-50">
            <CardHeader>
              <CardTitle className="text-3xl">⚠️ Deadly Mistakes to Avoid</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  {
                    mistake: "Buying bulk orders from one location",
                    why: "NYT has algorithms to detect and disqualify coordinated bulk purchases"
                  },
                  {
                    mistake: "Launching without a pre-built audience",
                    why: "You'll spend 10x more on ads to reach strangers vs. engaged followers"
                  },
                  {
                    mistake: "Spreading sales over multiple weeks",
                    why: "NYT list is based on ONE week's sales velocity, not cumulative total"
                  },
                  {
                    mistake: "Ignoring independent bookstores",
                    why: "They're heavily weighted in NYT algorithm - get on their shelves!"
                  },
                  {
                    mistake: "Launching during December or July",
                    why: "Highest competition + readers are distracted (holidays/vacation)"
                  },
                  {
                    mistake: "No PR strategy",
                    why: "Media coverage = social proof = more organic sales"
                  },
                  {
                    mistake: "Forgetting about eBook vs Print distinction",
                    why: "NYT has separate lists - pick your battlefield strategically"
                  }
                ].map((item, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-4 border border-gray-200">
                    <p className="font-semibold text-red-900 mb-1">❌ {item.mistake}</p>
                    <p className="text-sm text-gray-700">
                      <strong>Why it fails:</strong> {item.why}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* UPost's Role */}
          <Card className="mb-12 border-2 border-purple-300 bg-gradient-to-br from-purple-100 to-blue-100">
            <CardHeader>
              <CardTitle className="text-3xl">✍️ How UPost Helps Your Journey</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg text-gray-700 mb-6">
                While we can't guarantee a NYT Bestseller spot, UPost provides the tools to write 
                a professional-quality book faster:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 border border-purple-200">
                  <Award className="w-8 h-8 text-purple-600 mb-2" />
                  <h4 className="font-semibold text-gray-900 mb-2">AI Writing Assistant</h4>
                  <p className="text-sm text-gray-600">
                    Write your book 5x faster with AI that matches your voice
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-purple-200">
                  <BookOpen className="w-8 h-8 text-purple-600 mb-2" />
                  <h4 className="font-semibold text-gray-900 mb-2">Professional Editing</h4>
                  <p className="text-sm text-gray-600">
                    Polish your manuscript to traditional-publishing quality
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-purple-200">
                  <TrendingUp className="w-8 h-8 text-purple-600 mb-2" />
                  <h4 className="font-semibold text-gray-900 mb-2">Publishing Services</h4>
                  <p className="text-sm text-gray-600">
                    Amazon KDP setup + copyright protection included
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-purple-200">
                  <Mail className="w-8 h-8 text-purple-600 mb-2" />
                  <h4 className="font-semibold text-gray-900 mb-2">Marketing Resources</h4>
                  <p className="text-sm text-gray-600">
                    Templates, guides, and affiliate program to build your audience
                  </p>
                </div>
              </div>

              <div className="mt-6 text-center">
                <Link to="/register">
                  <Button size="lg" className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 px-8 py-6 text-lg">
                    Start Writing Your Bestseller →
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Bottom CTA */}
          <div className="text-center bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-12 text-white">
            <h2 className="text-3xl font-bold mb-4">
              Ready to Start Your Author Journey?
            </h2>
            <p className="text-xl mb-8 text-purple-100">
              Whether you're aiming for the NYT list or just want to share your story, 
              UPost gives you the tools to write professionally.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Link to="/register">
                <Button size="lg" variant="outline" className="bg-white text-purple-600 hover:bg-purple-50">
                  Get Started Free
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                  View Pricing Plans
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
      </div>
    </Layout>
  );
}
